import { Badge } from '@monksflow/monks-ui';
import { AlignLeftIcon, CalendarIcon, CircleUserIcon, TagIcon } from 'lucide-react';
import { AboutPageDialog } from '@/shared/ui';
import type { AboutPageDialogRow } from '@/shared/ui';
import { formatRelativeDate } from '@/shared/utils/dateUtils';
import { useTranslation } from 'react-i18next';
import { FileDetailProps } from './FileDetail.types';

/**
 * A document's metadata, rendered through the same dialog every other info
 * surface uses so the two read as one component rather than two designs.
 */
export const FileDetailV2 = ({ open, onClose, title, file }: FileDetailProps) => {
  const { t } = useTranslation('knowledge');

  const tags = Array.isArray(file.tags) ? file.tags : [];

  const rows: AboutPageDialogRow[] = [
    // A file with no summary has nothing to say here, and an empty row reads as
    // a rendering fault rather than as absent data.
    ...(file.summary
      ? [
          {
            icon: AlignLeftIcon,
            label: t('knowledgeBases.fileDetail.description'),
            text: file.summary,
          },
        ]
      : []),
    ...(file.createdByUsername
      ? [
          {
            icon: CircleUserIcon,
            label: t('knowledgeBases.fileDetail.addedBy'),
            text: file.createdByUsername,
          },
        ]
      : []),
    {
      icon: CalendarIcon,
      label: t('knowledgeBases.fileDetail.addedAt'),
      text: file.created
        ? formatRelativeDate(file.created)
        : t('knowledgeBases.fileDetail.unknown'),
    },
    {
      icon: TagIcon,
      label: t('knowledgeBases.fileDetail.tags'),
      text:
        tags.length > 0 ? (
          <span className="flex flex-wrap gap-2xs">
            {tags.map((tag) => (
              <Badge key={tag} variant="secondary" roundness="full">
                {tag}
              </Badge>
            ))}
          </span>
        ) : (
          t('knowledgeBases.fileDetail.noTags')
        ),
    },
  ];

  return (
    <AboutPageDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={title}
      rows={rows}
    />
  );
};

FileDetailV2.displayName = 'FileDetailV2';
